import { twMerge } from 'tailwind-merge';
import React, { forwardRef, InputHTMLAttributes } from 'react';
import Icon, { IconKey } from './Icon';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    iconKey?: IconKey;
    type?: 'text' | 'number' | 'password' | 'date' | 'email' | 'time';
    defaultValue?: string;
    autofocus?: boolean;
    /**
     * Rendered in the trailing-edge slot, before `iconKey`. Reserve room for it
     * with a `pe-*` on `className` — this component cannot know its width.
     */
    trailingSlot?: React.ReactNode;
}

/**
 * Never destructure `value` here and write it back as `value={...}`. That makes
 * it an own property even when the caller passed none, which sends React down
 * the `props.hasOwnProperty('value')` branch of `updateWrapper` and restores the
 * mount-time value instead of an updated `defaultValue` — silently breaking
 * every uncontrolled caller whose value arrives after mount. Let it ride the
 * spread. Covered by 'adopts a defaultValue that arrives after mount'.
 */
const BaseInput = forwardRef<HTMLInputElement, Props>(
    (
        {
            id,
            label = null,
            className = '',
            type = 'text',
            iconKey,
            defaultValue,
            trailingSlot,
            ...props
        },
        ref,
    ) => {
        return (
            <div className="flex w-full flex-col gap-y-1.5">
                {label && (
                    <label
                        htmlFor={id}
                        className="flex text-sm font-bold text-gray-800"
                    >
                        {label}
                    </label>
                )}
                <div className="relative w-full rounded-sm">
                    <input
                        id={id}
                        ref={ref}
                        type={type}
                        defaultValue={defaultValue}
                        // Only when there is no visible label to point at:
                        // otherwise the label above already names the field,
                        // and a second name would override what is on screen.
                        aria-label={label ? undefined : props.placeholder}
                        className={twMerge(
                            'form-input bg-gray-150 focus-ring block h-10 w-full rounded-sm border-0 p-3 text-sm/3 text-gray-800 placeholder:text-gray-500 read-only:bg-gray-100 focus:ring-0 disabled:text-gray-400 dark:bg-gray-100 dark:disabled:text-gray-600',
                            className,
                            iconKey ? 'pe-10' : '',
                        )}
                        {...props}
                    />
                    {trailingSlot || iconKey ? (
                        <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center gap-2 pe-3 text-gray-600">
                            {trailingSlot}
                            {iconKey ? (
                                <Icon iconKey={iconKey} size="24px" />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    },
);

export default BaseInput;
